import React, { useState, useEffect, useRef } from 'react';
import { ADMIN_CATEGORIES } from './adminHoursData';
import { q3gRate, q3gWeekKey } from './q3gRates';

const PATHS = ['PID', 'PR', 'MANR', 'SKID'];
const PATH_COLORS = { PID: '#1565c0', PR: '#2e7d32', MANR: '#e65100', SKID: '#6a1b9a' };
const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const PROD_DOORS = {
  PID: ['DD352','DD353','DD354','DD355','DD356','DD357','DD358','DD359','DD360'],
  PR: ['DD143','DD146','DD150','DD153','DD156'],
  SKID: ['DD135','DD136','DD137','DD138'],
  MANR: ['DD347','DD348'],
};

function extractCartons(notes) {
  const m = (notes || '').match(/IBVEND\s+\w+\s+(\d+)/i);
  return m ? parseInt(m[1]) : 0;
}

export default function Midi({ api, site }) {
  const [date, setDate] = useState('');
  const [shiftType, setShiftType] = useState('day');
  const [yardData, setYardData] = useState(null);
  const [shiftSnapshot, setShiftSnapshot] = useState(null);
  const [lvpPlan, setLvpPlan] = useState(null);
  const [fclmData, setFclmData] = useState(null);
  const [midiPlan, setMidiPlan] = useState(null);
  const [midiActual, setMidiActual] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bridges, setBridges] = useState({ cartons: '', cplh: '', hours: '' });
  const [standardWork, setStandardWork] = useState({});
  const [swBridge, setSwBridge] = useState('');
  const [swCompletionPct, setSwCompletionPct] = useState(null); // SWC tab completion % for this date/shift (all quarters)
  const [swCollapsed, setSwCollapsed] = useState(true);
  const [showSupportBreakdown, setShowSupportBreakdown] = useState(false);
  const [bridgeSaved, setBridgeSaved] = useState(false);
  const midiCaptureRef = useRef(null);
  const [copyMsg, setCopyMsg] = useState('');

  // Capture the top two tables + trailer plan + bridge notes to a PNG on the
  // clipboard. Uses native SVG <foreignObject> (no html2canvas dependency).
  // Textareas don't serialize their live value into HTML, so we clone the node
  // and replace each <textarea> with a <div> holding its current text.
  const copyMidiImage = async () => {
    const src = midiCaptureRef.current;
    if (!src) return;
    try {
      const clone = src.cloneNode(true);
      // Replace textareas (bridge notes) with divs so their text renders
      const srcTAs = src.querySelectorAll('textarea');
      const cloneTAs = clone.querySelectorAll('textarea');
      cloneTAs.forEach((ta, i) => {
        const div = document.createElement('div');
        div.textContent = srcTAs[i] ? srcTAs[i].value : ta.value;
        div.setAttribute('style', 'white-space:pre-wrap;font-size:0.85rem;padding:6px;border:1px solid #ccc;border-radius:4px;min-height:60px;background:#fff;');
        ta.parentNode.replaceChild(div, ta);
      });
      // Replace inputs (Standard Work editable cells) with divs holding their
      // current value — input value is a DOM property, not a serialized
      // attribute, so it would otherwise render blank in the SVG capture.
      const srcInputs = src.querySelectorAll('input');
      const cloneInputs = clone.querySelectorAll('input');
      cloneInputs.forEach((inp, i) => {
        const div = document.createElement('div');
        div.textContent = srcInputs[i] ? srcInputs[i].value : inp.value;
        div.setAttribute('style', 'font-size:0.68rem;text-align:right;');
        inp.parentNode.replaceChild(div, inp);
      });
      const rect = src.getBoundingClientRect();
      const w = Math.ceil(rect.width), h = Math.ceil(clone.scrollHeight || rect.height);
      clone.setAttribute('xmlns', 'http://www.w3.org/1999/xhtml');
      const html = new XMLSerializer().serializeToString(clone);
      const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='${w}' height='${h}'><foreignObject width='100%' height='100%'><div xmlns='http://www.w3.org/1999/xhtml' style='background:#fff;font-family:Arial,sans-serif'>${html}</div></foreignObject></svg>`;
      const img = new Image();
      const svgUrl = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
      await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = svgUrl; });
      const dpr = window.devicePixelRatio || 1;
      const cv = document.createElement('canvas');
      cv.width = w * dpr; cv.height = h * dpr;
      const ctx = cv.getContext('2d');
      ctx.scale(dpr, dpr);
      ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, w, h);
      ctx.drawImage(img, 0, 0);
      cv.toBlob(async (blob) => {
        if (!blob) { setCopyMsg('Copy failed'); setTimeout(() => setCopyMsg(''), 4000); return; }
        try {
          await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': blob })]);
          setCopyMsg('Copied to clipboard ✓');
        } catch (e) {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a'); a.href = url; a.download = `midi_${site}_${date}_${shiftType}.png`; a.click();
          URL.revokeObjectURL(url);
          setCopyMsg('Clipboard blocked — downloaded PNG');
        }
        setTimeout(() => setCopyMsg(''), 4000);
      }, 'image/png');
    } catch (e) {
      console.error(e);
      setCopyMsg('Copy failed — see console');
      setTimeout(() => setCopyMsg(''), 4000);
    }
  };

  useEffect(() => {
    const now = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));
    setDate(`${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`);
    setShiftType(now.getHours() >= 8 && now.getHours() < 19 ? 'day' : 'night');
  }, []);

  useEffect(() => {
    if (!date) return;
    setLoading(true);
    const d = new Date(date + 'T12:00:00');
    const dayOfWeek = d.getDay();
    const sunday = new Date(d); sunday.setDate(d.getDate() - dayOfWeek);
    const weekStart = `${sunday.getFullYear()}-${String(sunday.getMonth()+1).padStart(2,'0')}-${String(sunday.getDate()).padStart(2,'0')}`;
    const dayName = DAYS[dayOfWeek];
    const shiftKey = `${dayName}_${shiftType}`;

    Promise.all([
      fetch(`${api}/api/fclm/cache?site=${site}&date=YMS_YARD&shift_type=current`).then(r=>r.json()).catch(()=>null),
      fetch(`${api}/api/fclm/cache?site=${site}&date=YMS_SHIFT_${date}&shift_type=${shiftType}`).then(r=>r.json()).catch(()=>null),
      fetch(`${api}/api/fclm/cache?site=${site}&date=LVP_${weekStart}&shift_type=plan`).then(r=>r.json()).catch(()=>null),
      fetch(`${api}/api/fclm/cache?site=${site}&date=${date}&shift_type=${shiftType}`).then(r=>r.json()).catch(()=>null),
      fetch(`${api}/api/fclm/cache?site=${site}&date=MIDI_PLAN_${date}&shift_type=${shiftType}`).then(r=>r.json()).catch(()=>null),
      fetch(`${api}/api/fclm/cache?site=${site}&date=MIDI_ACTUAL_${date}&shift_type=${shiftType}`).then(r=>r.json()).catch(()=>null),
      fetch(`${api}/api/fclm/cache?site=${site}&date=MIDI_BRIDGE_${date}&shift_type=${shiftType}`).then(r=>r.json()).catch(()=>null),
      fetch(`${api}/api/fclm/cache?site=${site}&date=MIDI_STDWORK_${date}&shift_type=${shiftType}`).then(r=>r.json()).catch(()=>null),
      fetch(`${api}/api/fclm/cache?site=${site}&date=SWC_${date}&shift_type=${shiftType}`).then(r=>r.json()).catch(()=>null),
    ]).then(([yard, snapshot, lvp, fclm, midiP, midiA, bridgeData, swData, swcData]) => {
      setYardData(yard?.trailers ? yard : null);
      setShiftSnapshot(snapshot?.completions ? snapshot : null);
      // SWC Completion %: mirrors the SWC tab donut — checked items across all 4
      // quarters / (14 checklist items * 4 quarters). Null if no SWC record yet.
      (() => {
        const checks = swcData && swcData.checks;
        if (!checks || typeof checks !== 'object') { setSwCompletionPct(null); return; }
        const QS = ['Q1', 'Q2', 'Q3', 'Q4'];
        const SWC_ITEMS = 14; // keep in sync with SWC.js TASKS length
        let checked = 0;
        // Support both quarter-keyed { Q1:{id:{checked}} } and legacy flat { id:{checked} }.
        const quarterKeyed = QS.some(q => checks[q] && typeof checks[q] === 'object');
        if (quarterKeyed) {
          QS.forEach(q => { const qc = checks[q] || {}; Object.values(qc).forEach(v => { if (v && v.checked) checked++; }); });
        } else {
          Object.values(checks).forEach(v => { if (v && v.checked) checked++; });
        }
        const slots = SWC_ITEMS * QS.length;
        setSwCompletionPct(slots > 0 ? Math.round((checked / slots) * 100) : 0);
      })();
      setMidiPlan(midiP?.planTrailers ? midiP : null);
      setMidiActual(midiA?.completions ? midiA : null);
      if (bridgeData && typeof bridgeData.cartons === 'string') {
        setBridges({ cartons: bridgeData.cartons || '', cplh: bridgeData.cplh || '', hours: bridgeData.hours || '' });
      } else {
        setBridges({ cartons: '', cplh: '', hours: '' });
      }
      if (swData && (swData.values || swData.bridge != null)) {
        setStandardWork(swData.values || {});
        setSwBridge(swData.bridge || '');
      } else {
        setStandardWork({});
        setSwBridge('');
      }
      if (lvp?.shiftData) {
        const plan = lvp.shiftData[shiftKey] || {};
        const fpp = lvp.fppRatios || {};
        const totalCartons = plan.cartons || 0;
        const pidC = plan.pidCartons ? parseInt(plan.pidCartons) : Math.round(totalCartons * (fpp.pid || 0));
        const arosC = plan.arosCartons ? parseInt(plan.arosCartons) : Math.round(totalCartons * (fpp.aros || 0));
        const prC = totalCartons - pidC - arosC;
        setLvpPlan({ totalCartons, pidCartons: pidC, arosCartons: arosC, prCartons: prC, skidCartons: Math.round(totalCartons * (fpp.skid || 0)), hours: plan.hours || 0, ibHours: lvp.ibHours?.[shiftKey] || 0, daHours: lvp.daHours?.[shiftKey] || 0, plannedHours: lvp.plannedHours?.[shiftKey] || 0, expectedHours: lvp.expectedHours?.[shiftKey] || 0, adminHours: parseFloat(lvp.adminHours?.[shiftKey]) || 0, adminDetail: lvp.adminDetail || {}, shiftKey, eolPct: lvp.eolPct || 33, cpp: lvp.cpp || 30.98, computedHours: lvp.computedHours, overrides: lvp.overrides, rateSource: lvp.rateSource, weekStart });
      } else setLvpPlan(null);
      setFclmData(fclm?.labor ? fclm : null);
      setLoading(false);
    });
  }, [api, site, date, shiftType]);

  // Helpers

  const saveBridges = async () => {
    await fetch(`${api}/api/fclm/cache`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ site, date: `MIDI_BRIDGE_${date}`, shift_type: shiftType, data: bridges }),
    });
    setBridgeSaved(true);
    setTimeout(() => setBridgeSaved(false), 3000);
  };

  const saveStandardWork = async () => {
    await fetch(`${api}/api/fclm/cache`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ site, date: `MIDI_STDWORK_${date}`, shift_type: shiftType, data: { values: standardWork, bridge: swBridge } }),
    });
    setBridgeSaved(true);
    setTimeout(() => setBridgeSaved(false), 3000);
  };

  const getOnDoor = (path) => (yardData?.trailers || []).filter(t => t.path === path && (PROD_DOORS[path] || []).some(d => t.location?.startsWith(d)));
  const getYardQueue = (path) => {
    const allDoors = Object.values(PROD_DOORS).flat();
    return (yardData?.trailers || []).filter(t => t.path === path && !t.isDefect && !allDoors.some(d => t.location?.startsWith(d))).sort((a, b) => b.dwellHours - a.dwellHours);
  };
  // Get shift time boundaries for filtering completions
  const getShiftBounds = () => {
    if (!date) return { start: 0, end: Infinity };
    const d = new Date(date + 'T12:00:00');
    if (shiftType === 'day') {
      const start = new Date(date + 'T08:00:00-07:00').getTime() / 1000;
      const end = new Date(date + 'T19:00:00-07:00').getTime() / 1000;
      return { start, end };
    } else {
      const start = new Date(date + 'T19:00:00-07:00').getTime() / 1000;
      const nextDay = new Date(d); nextDay.setDate(d.getDate() + 1);
      const end = new Date(nextDay.toISOString().split('T')[0] + 'T06:00:00-07:00').getTime() / 1000;
      return { start, end };
    }
  };
  const shiftBounds = getShiftBounds();

  const getCompletions = (path) => {
    // Use MIDI_ACTUAL as primary source (has proper paths), fallback to shift snapshot
    const comps = midiActual?.completions || shiftSnapshot?.completions || [];
    // Defensive dedup — the YMS event source can return each completion twice
    const seen = new Set();
    const uniq = [];
    for (const c of comps) {
      const k = `${c.isaId || ''}|${c.visitId || ''}|${c.vehicle || ''}|${Math.round(Number(c.completionTime) || 0)}`;
      if (seen.has(k)) continue;
      seen.add(k);
      uniq.push(c);
    }
    return uniq.filter(c => c.path === path && c.completionTime >= shiftBounds.start && c.completionTime <= shiftBounds.end).sort((a,b) => b.dwellHours - a.dwellHours);
  };
  const getActualCartons = (key) => fclmData?.cartons?.[key]?.total || 0;
  // Actual pallets received = PPR Pallet Receive line volume (same as the PPR homepage /
  // function rollup). Falls back to PR cartons / CPP only if that line isn't cached yet.
  const PALLET_RECEIVE_LINE = 'ppr.detail.inbound.receive.palletReceive';
  const actualPallets = (() => {
    const line = fclmData?.ppr_lines?.[PALLET_RECEIVE_LINE];
    if (line && typeof line === 'object') {
      const vol = Object.values(line).reduce((t, v) => t + (Array.isArray(v) ? (parseFloat(v[0]) || 0) : 0), 0);
      if (vol > 0) return Math.round(vol);
    }
    return Math.round(getActualCartons('pr') / (lvpPlan?.cpp || 30.98));
  })();
  const getPlannedCartons = (path) => ({ PID: lvpPlan?.pidCartons, PR: lvpPlan?.prCartons, MANR: lvpPlan?.arosCartons, SKID: lvpPlan?.skidCartons })[path] || 0;
  // Link a trailer identifier to its YMS event history (by numeric visitId)
  const evtUrl = (visitId) => { const v = String(visitId || '').replace(/^V/, ''); if (!v) return null; const to = Date.now(); const from = to - 30 * 86400000; return `https://trans-logistics.amazon.com/yms/eventHistory#/eventReport?yard=${site}&fromDate=${from}&toDate=${to}&visitId=${v}`; };
  const idLink = (text, visitId) => { const u = evtUrl(visitId); return u ? <a href={u} target="_blank" rel="noopener noreferrer" style={{ color: '#1565c0', textDecoration: 'underline' }}>{text}</a> : text; };

  // Actual hours/rates from FCLM
  const ibHours = fclmData?.ppr_hours ? Object.values(fclmData.ppr_hours.inbound || {}).reduce((s,v) => s + (typeof v === 'number' ? v : 0), 0) : 0;
  const obHours = fclmData?.ppr_hours ? Object.values(fclmData.ppr_hours.transferOut || {}).reduce((s,v) => s + (typeof v === 'number' ? v : 0), 0) : 0;
  const totalHours = fclmData?.ppr_hours ? Object.values(fclmData.ppr_hours.fcSummary || {}).reduce((s,v) => s + (typeof v === 'number' ? v : 0), 0) : 0;
  const directHours = ibHours + obHours;
  const laborTotal = (fclmData?.labor || []).reduce((s, l) => s + (l.total || 0), 0);
  const supportHours = totalHours - laborTotal;

  // Support-hours breakdown (shift totals) from the PPR Support section
  const sumHrs = (obj) => obj ? Object.values(obj).reduce((s, v) => s + (typeof v === 'number' ? v : 0), 0) : 0;
  const adminActualHrs = sumHrs(fclmData?.support_hours?.admin);
  const fcncActualHrs = sumHrs(fclmData?.support_hours?.nonFcControllable);
  const onboardingActualHrs = sumHrs(fclmData?.support_hours?.onboarding);
  const ibCartons = getActualCartons('inbound');
  const obCartons = getActualCartons('outbound');
  const totalCartons = ibCartons + obCartons;

  // Plan hours from LVP computedHours by dept
  const sk = lvpPlan?.shiftKey || "";
  const ch = lvpPlan?.computedHours || {};
  // Prefer the LVP plan's authoritative per-shift hours; fall back to computedHours dept sums for legacy plans
  const legacyIB = (ch["PID"]?.[sk] || 0) + (ch["IB PR"]?.[sk] || 0) + (ch["IB PIT"]?.[sk] || 0) + (ch["IB Support"]?.[sk] || 0);
  const legacyOB = (ch["Sorter EOL"]?.[sk] || 0) + (ch["Fluid"]?.[sk] || 0) + (ch["OBPR"]?.[sk] || 0) + (ch["OB Support"]?.[sk] || 0);
  const planIBHours = lvpPlan?.ibHours || legacyIB;
  const planOBHours = lvpPlan?.daHours || legacyOB;
  const planDirectHours = lvpPlan?.plannedHours || (planIBHours + planOBHours);
  // All In (Diluted) = Direct (IB+DA) + Admin — matches the LVP's Diluted CPLH denominator,
  // so it's always >= Direct hours and the diluted rate is always <= the direct rate.
  const planAllInHours = planDirectHours + (lvpPlan?.adminHours || 0);
  const planSupportHours = planAllInHours - planDirectHours;

  // Q3G (Finance-locked) goals when the week's LVP plan is in Q3G mode. Goal hours are
  // derived from the same rates (cartons / rate) so the Hours and CPLH goals agree.
  const q3gOn = lvpPlan?.rateSource === 'q3g' && !!q3gWeekKey(lvpPlan?.weekStart);
  const goal = (() => {
    const c = lvpPlan?.totalCartons || 0;
    if (!q3gOn || !c) {
      return {
        allInCplh: planAllInHours > 0 ? (c * 2) / planAllInHours : 0,
        directCplh: planDirectHours > 0 ? (c * 2) / planDirectHours : 0,
        ibCplh: planIBHours > 0 ? c / planIBHours : 0,
        obCplh: planOBHours > 0 ? c / planOBHours : 0,
        allInHours: planAllInHours, directHours: planDirectHours, ibHours: planIBHours, obHours: planOBHours, supportHours: planSupportHours,
      };
    }
    const ib = q3gRate('602', lvpPlan.weekStart), da = q3gRate('604', lvpPlan.weekStart), tp = q3gRate('609', lvpPlan.weekStart);
    const ibH = ib > 0 ? c / ib : planIBHours, obH = da > 0 ? c / da : planOBHours;
    const allH = tp > 0 ? (c * 2) / tp : planAllInHours;
    return { allInCplh: tp, directCplh: (ibH + obH) > 0 ? (c * 2) / (ibH + obH) : 0, ibCplh: ib, obCplh: da,
             allInHours: allH, directHours: ibH + obH, ibHours: ibH, obHours: obH, supportHours: Math.max(0, allH - ibH - obH) };
  })();

  // Next-up queue
  const getCartonsNeeded = (path) => {
    const planned = getPlannedCartons(path);
    const onDoorC = getOnDoor(path).reduce((s, t) => s + extractCartons(t.notes) * 0.5, 0);
    const completedC = getCompletions(path).reduce((s, c) => s + extractCartons(c.notes), 0);
    return Math.max(0, planned - completedC - onDoorC);
  };
  const getNextUp = (path) => {
    const needed = getCartonsNeeded(path);
    const queue = getYardQueue(path);
    const result = []; let running = 0;
    for (const t of queue) { const c = extractCartons(t.notes); result.push({...t, cartons: c, runningTotal: running + c}); running += c; if (running >= needed) break; }
    return result;
  };

  // FIFO check for completions
  const isInPlan = (completion) => {
    // Was this trailer the oldest dwelling for its path at time of completion?
    // Check: are there other trailers in yard (not on door) with LONGER dwell?
    const path = completion.path;
    const queue = getYardQueue(path);
    const onDoor = getOnDoor(path);
    const onDoorVehicles = onDoor.map(t => t.vehicle);
    // If there's a trailer in yard (not on door) with longer dwell than this completion, it's out of plan
    const olderInYard = queue.filter(t => t.dwellHours > completion.dwellHours && !onDoorVehicles.includes(t.vehicle));
    return olderInYard.length === 0;
  };

  const cellStyle = { padding: '4px 8px', textAlign: 'center', fontSize: '0.7rem', border: '1px solid #000', whiteSpace: 'nowrap' };
  const headerStyle = { ...cellStyle, fontWeight: 700, background: '#232f3e', color: '#fff' };
  const labelStyle = { ...cellStyle, textAlign: 'left', fontWeight: 600, background: '#f5f5f5' };

  const deltaColor = (actual, plan) => !plan ? '#333' : actual >= plan ? '#4caf50' : '#ef5350';
  const fmt = (v) => v ? Math.round(v).toLocaleString() : '—';

  return (
    <div>
      <h2 className="section-title">Midichlorians — {site}</h2><p style={{ fontSize: "0.7rem", color: "#666", margin: "-8px 0 8px", fontStyle: "italic" }}>Managed Inbound &amp; Door Instructions for Cargo Handling, Load Optimization, Recording, Insight, Analytics, Next-door Suggestions</p>
      <div className="card">
        <div className="form-row">
          <div className="form-group"><label>Date</label><input type="date" value={date} onChange={e => setDate(e.target.value)} /></div>
          <div className="form-group"><label>Shift</label><select value={shiftType} onChange={e => setShiftType(e.target.value)}><option value="day">Day</option><option value="night">Night</option></select></div>
          <div className="form-group"><button className="btn btn-primary" onClick={saveBridges} style={{ fontSize: '0.7rem', padding: '5px 12px' }}>{bridgeSaved ? '✅ Saved!' : '💾 Save Bridges'}</button></div>
          <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button className="btn" onClick={copyMidiImage} disabled={loading} style={{ background: '#455a64', color: '#fff', border: 'none', padding: '5px 12px', borderRadius: 4, cursor: 'pointer', fontWeight: 500, fontSize: '0.7rem' }}>📋 Copy Image</button>
            {copyMsg && <span style={{ fontSize: '0.7rem', color: '#2e7d32' }}>{copyMsg}</span>}
          </div>
        </div>
      </div>

      {!loading && (
        <>
          <div ref={midiCaptureRef}>
          {/* Section 1: Cartons */}
          <div className="card" style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: '2 1 0', overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', fontSize: '0.75rem' }}>
              <thead><tr><th style={{ ...headerStyle, textAlign: 'left', minWidth: 160 }}>Cartons</th><th style={headerStyle}>Goal</th><th style={headerStyle}>Actual</th><th style={headerStyle}>Δ</th></tr></thead>
              <tbody>
                {[
                  { label: 'Total IB Cartons', goal: lvpPlan?.totalCartons, actual: ibCartons },
                  { label: 'Total OB Cartons', goal: lvpPlan?.totalCartons, actual: obCartons },
                  { label: 'Sorter Cartons', goal: lvpPlan?.pidCartons, actual: getActualCartons('pid') },
                  { label: 'AROS Cartons', goal: lvpPlan?.arosCartons, actual: getActualCartons('aros') },
                  { label: 'PR Cartons', goal: lvpPlan?.prCartons, actual: getActualCartons('pr') },
                  { label: 'PR Pallets', goal: lvpPlan ? Math.round(lvpPlan.prCartons / (lvpPlan.cpp || 30.98)) : 0, actual: actualPallets },
                  { label: 'Fluid Cartons', goal: lvpPlan ? Math.round(lvpPlan.pidCartons * (1 - (lvpPlan.eolPct || 33)/100)) + Math.round(lvpPlan.pidCartons * (lvpPlan.eolPct||33)/100) + Math.round(lvpPlan.arosCartons) : 0, actual: (getActualCartons('fluidDirect') || 0) + (getActualCartons('fluidOffline') || 0) },
                  { label: 'PIT Cartons', goal: lvpPlan?.prCartons || 0, actual: getActualCartons('pit') },
                ].map(row => (
                  <tr key={row.label}>
                    <td style={labelStyle}>{row.label}</td>
                    <td style={cellStyle}>{fmt(row.goal)}</td>
                    <td style={cellStyle}>{fmt(row.actual)}</td>
                    <td style={{ ...cellStyle, color: deltaColor(row.actual, row.goal), fontWeight: 600 }}>{row.goal ? (row.actual - row.goal > 0 ? '+' : '') + Math.round(row.actual - row.goal).toLocaleString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            <div style={{ flex: '1 1 0', minWidth: 150 }}>
              <label style={{ fontSize: '0.65rem', fontWeight: 600, color: '#666' }}>Cartons Bridge</label>
              <textarea rows={6} placeholder="Notes / bridge..." value={bridges.cartons} onChange={e => setBridges(prev => ({...prev, cartons: e.target.value}))} style={{ width: '100%', fontSize: '0.85rem', padding: 6, border: '1px solid #ccc', borderRadius: 4, resize: 'vertical' }} />
            </div>
          </div>

          {/* Section 2: CPLH & Hours */}
          <div className="card" style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: '2 1 0', overflowX: 'auto' }}>
              <table style={{ borderCollapse: 'collapse', fontSize: '0.75rem', width: '100%' }}>
                <thead><tr><th style={{ ...headerStyle, textAlign: 'left' }}>Metric</th><th style={headerStyle}>Goal</th><th style={headerStyle}>Actual</th><th style={headerStyle}>% / Δ</th></tr></thead>
                <tbody>
                  {[
                    { label: q3gOn ? 'All In (Diluted) — Q3G' : 'All In (Diluted)', goal: goal.allInCplh, actual: totalHours > 0 ? totalCartons / totalHours : 0, isRate: true },
                    { label: 'Direct Throughput (CPLH)', goal: goal.directCplh, actual: directHours > 0 ? totalCartons / directHours : 0, isRate: true },
                    { label: 'IB CPLH', goal: goal.ibCplh, actual: ibHours > 0 ? ibCartons / ibHours : 0, isRate: true },
                    { label: 'OB CPLH', goal: goal.obCplh, actual: obHours > 0 ? obCartons / obHours : 0, isRate: true },
                  ].map(row => (
                    <tr key={row.label}>
                      <td style={labelStyle}>{row.label}</td>
                      <td style={cellStyle}>{row.goal ? row.goal.toFixed(1) : '—'}</td>
                      <td style={cellStyle}>{row.actual ? row.actual.toFixed(1) : '—'}</td>
                      <td style={{ ...cellStyle, fontWeight: 600 }}>{row.goal && row.actual ? Math.round(row.actual / row.goal * 100) + '%' : '—'}</td>
                    </tr>
                  ))}
                  <tr style={{ borderTop: '2px solid #000' }}><td colSpan={4} style={{ ...cellStyle, background: '#f5f5f5', fontWeight: 700, textAlign: 'left' }}>Hours</td></tr>
                  {[
                    { label: 'Total Throughput', goal: goal.allInHours, actual: totalHours },
                    { label: 'Direct Hours', goal: goal.directHours, actual: directHours },
                    { label: 'IB Hours', goal: goal.ibHours, actual: ibHours },
                    { label: 'OB Hours', goal: goal.obHours, actual: obHours },
                  ].map(row => (
                    <tr key={row.label}>
                      <td style={labelStyle}>{row.label}</td>
                      <td style={cellStyle}>{fmt(row.goal)}</td>
                      <td style={cellStyle}>{fmt(row.actual)}</td>
                      <td style={{ ...cellStyle, color: row.goal ? deltaColor(row.goal, row.actual) : '#333', fontWeight: 600 }}>{row.goal ? Math.round(row.actual - row.goal).toLocaleString() : '—'}</td>
                    </tr>
                  ))}
                  {/* Support Hours — click to expand the three PPR support buckets */}
                  <tr onClick={() => setShowSupportBreakdown(v => !v)} style={{ cursor: 'pointer' }}>
                    <td style={{ ...labelStyle, userSelect: 'none' }}>{showSupportBreakdown ? '▼' : '▶'} Support Hours</td>
                    <td style={cellStyle}>{fmt(goal.supportHours > 0 ? goal.supportHours : 0)}</td>
                    <td style={cellStyle}>{fmt(supportHours > 0 ? supportHours : 0)}</td>
                    <td style={{ ...cellStyle, color: goal.supportHours > 0 ? deltaColor(goal.supportHours, supportHours) : '#333', fontWeight: 600 }}>{goal.supportHours > 0 ? Math.round((supportHours > 0 ? supportHours : 0) - goal.supportHours).toLocaleString() : '—'}</td>
                  </tr>
                  {showSupportBreakdown && [
                    { label: 'Admin', actual: adminActualHrs },
                    { label: 'FCNC (Non_FC_Controllable)', actual: fcncActualHrs },
                    { label: 'On Boarding', actual: onboardingActualHrs },
                  ].map(row => (
                    <tr key={row.label} style={{ background: '#fafafa' }}>
                      <td style={{ ...labelStyle, paddingLeft: 22, fontWeight: 400, color: '#555' }}>{row.label}</td>
                      <td style={cellStyle}>—</td>
                      <td style={cellStyle}>{fmt(row.actual)}</td>
                      <td style={cellStyle}>—</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ flex: '1 1 0', minWidth: 150 }}>
              <label style={{ fontSize: '0.65rem', fontWeight: 600, color: '#666' }}>CPLH / Hours Bridge</label>
              <textarea rows={8} placeholder="Notes / bridge..." value={bridges.cplh} onChange={e => setBridges(prev => ({...prev, cplh: e.target.value}))} style={{ width: '100%', fontSize: '0.85rem', padding: 6, border: '1px solid #ccc', borderRadius: 4, resize: 'vertical' }} />
            </div>
          </div>
          {/* Standard Work table */}
          {(() => {
            const SW_LEFT = [
              { k: 'incidents', label: 'Incidents', g: '0', lower: true },
              { k: 'nearMisses', label: 'Near Misses', g: '0', lower: true },
              { k: 'simbas', label: 'SIMBAS (WKTD)', g: '80' },
              { k: 'instacleans', label: 'Instacleans Completed', g: '1' },
              { k: 'palletHawk', label: 'Pallet Hawk Comliance.', pct: true, g: '100' },
              { k: 'gemba', label: 'Gemba Audit (Mon-Fri Only)', g: '1' },
              { k: 'arcs', label: 'ARCs Completed', g: '5' },
              { k: 'rbis', label: "RBI's Completed", g: '10' },
              { k: 'dwellIsa', label: 'Dwelling ISAs> 8 Days', g: '0', lower: true },
              { k: 'wwHuddle', label: 'WW Huddle Completion', pct: true, g: '95' },
              { k: 'wwHuddleRef', label: 'WW Huddle Completion - Refresher', pct: true, g: '59' },
              { k: 'wwHuddleLabor', label: "WW Huddle AA's Labor Tracked >1 Hour", pct: true },
              { k: 'engagement', label: 'Engagement Campaign', pct: true, g: '100' },
              { k: 'unapprovedAccom', label: 'Unapproved Accom Hours', g: '0', lower: true },
              { k: 'timeNotLogged', label: 'Time Not Logged (Prior 7 Days)', lower: true },
            ];
            const SW_RIGHT = [
              { k: 'piles', label: 'Piles', g: '500', lower: true },
              { k: 'pilesAudits', label: 'Piles Audits Completed', g: '4' },
              { k: 'atlasItems', label: 'Atlas Items Generated', g: '0', lower: true },
              { k: 'ibTrailerAudits', label: 'IB Trailer Audits', g: '4' },
              { k: 'shipFailedDpmo', label: 'Ship Failed Moves DPMO', g: '3000', lower: true },
              { k: 'guidedCoaching', label: 'Guided Coaching Actions', g: '0', lower: true },
              { k: 'opsRecirc', label: 'Ops Recirc %', pct: true, g: '2', lower: true },
              { k: 'cageAudits', label: 'Cage Audits', g: '8' },
            ];
            const swCell = { border: '1px solid #999', padding: '2px 6px', fontSize: '0.68rem', whiteSpace: 'nowrap' };
            const swHdr = { ...swCell, background: '#f0f0f0', fontWeight: 700, textAlign: 'center' };
            const labelCell = { ...swCell, background: '#d8d2cb', fontWeight: 700, textAlign: 'center', width: 90 };
            const num = (v) => (v === '' || v == null || isNaN(parseFloat(v))) ? null : parseFloat(v);
            const setSW = (k, field, val) => setStandardWork(prev => ({ ...prev, [k]: { ...(prev[k] || {}), [field]: val } }));
            const editStyle = { width: '100%', border: 'none', background: 'transparent', fontSize: '0.68rem', textAlign: 'right', boxSizing: 'border-box' };
            const yellow = '#fffec8';
            return (
              <div className="card" style={{ marginBottom: 8, overflowX: 'auto' }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4, gap: 8 }}>
                  <h3 className="section-title" style={{ margin: 0, cursor: 'pointer', userSelect: 'none' }} onClick={() => setSwCollapsed(c => !c)}>{swCollapsed ? '▶' : '▼'} Standard Work</h3>
                  {!swCollapsed && <button className="btn" onClick={saveStandardWork} style={{ background: '#455a64', color: '#fff', border: 'none', padding: '4px 10px', borderRadius: 4, cursor: 'pointer', fontWeight: 500, fontSize: '0.7rem' }}>{bridgeSaved ? '✅ Saved!' : '💾 Save'}</button>}
                </div>
                {!swCollapsed && (
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                  {/* Left block: Safety/Standard Work */}
                  <table style={{ borderCollapse: 'collapse' }}>
                    <thead><tr><th style={labelCell}></th><th style={swHdr}>Metric</th><th style={swHdr}>Goal</th><th style={swHdr}>Actual</th><th style={swHdr}>Delta</th></tr></thead>
                    <tbody>
                      {SW_LEFT.map((r, i) => (
                        <React.Fragment key={r.k}>
                        <tr>
                          {i === 0 && <td rowSpan={SW_LEFT.length + 1} style={labelCell}>Safety/Standard Work</td>}
                          <td style={swCell}>{r.label}</td>
                          <td style={{ ...swCell, background: yellow, textAlign: 'right', width: 52 }}><input value={(standardWork[r.k]||{}).goal ?? (r.g ?? '')} onChange={e => setSW(r.k, 'goal', e.target.value)} style={editStyle} /></td>
                          <td style={{ ...swCell, background: yellow, textAlign: 'right', width: 52 }}><input value={(standardWork[r.k]||{}).actual ?? ''} onChange={e => setSW(r.k, 'actual', e.target.value)} style={editStyle} /></td>
                          {(() => { const row=standardWork[r.k]||{}; const g=num(row.goal ?? r.g),a=num(row.actual); let t='',bg=''; if(g!=null&&a!=null){const d=a-g;t=r.pct?`${Math.round(d)}%`:(Number.isInteger(d)?d:d.toFixed(1));const good=r.lower?(a<=g):(a>=g);bg=good?'#e2efda':'#fbe4e4';}else if(a!=null){t=r.pct?`${Math.round(a)}%`:a.toFixed(1);bg='#fbe4e4';} return <td style={{ ...swCell, background: bg, textAlign: 'right', width: 52 }}>{t}</td>; })()}
                        </tr>
                        {r.k === 'nearMisses' && (() => {
                          // SWC Completion % — pulled from the SWC tab for this date/shift.
                          // Goal fixed 100%, actual = computed %, delta = actual - 100. No overrides.
                          const a = swCompletionPct;
                          const g = 100;
                          const have = a != null;
                          const bg = have ? (a >= g ? '#e2efda' : '#fbe4e4') : '';
                          return (
                            <tr>
                              <td style={swCell}>SWC Completion %</td>
                              <td style={{ ...swCell, textAlign: 'right', width: 52, color: '#666' }}>100%</td>
                              <td style={{ ...swCell, textAlign: 'right', width: 52 }}>{have ? `${a}%` : '—'}</td>
                              <td style={{ ...swCell, background: bg, textAlign: 'right', width: 52 }}>{have ? `${a - g}%` : '—'}</td>
                            </tr>
                          );
                        })()}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                  {/* Right block: Quatily */}
                  <table style={{ borderCollapse: 'collapse' }}>
                    <thead><tr><th style={labelCell}></th><th style={swHdr}>Metric</th><th style={swHdr}>Goal</th><th style={swHdr}>Actual</th><th style={swHdr}>Delta</th></tr></thead>
                    <tbody>
                      {SW_RIGHT.map((r, i) => (
                        <tr key={r.k}>
                          {i === 0 && <td rowSpan={SW_RIGHT.length} style={labelCell}>Quatily</td>}
                          <td style={swCell}>{r.label}</td>
                          <td style={{ ...swCell, background: yellow, textAlign: 'right', width: 52 }}><input value={(standardWork[r.k]||{}).goal ?? (r.g ?? '')} onChange={e => setSW(r.k, 'goal', e.target.value)} style={editStyle} /></td>
                          <td style={{ ...swCell, background: yellow, textAlign: 'right', width: 52 }}><input value={(standardWork[r.k]||{}).actual ?? ''} onChange={e => setSW(r.k, 'actual', e.target.value)} style={editStyle} /></td>
                          {(() => { const row=standardWork[r.k]||{}; const g=num(row.goal ?? r.g),a=num(row.actual); let t='',bg=''; if(g!=null&&a!=null){const d=a-g;t=r.pct?`${Math.round(d)}%`:(Number.isInteger(d)?d:d.toFixed(1));const good=r.lower?(a<=g):(a>=g);bg=good?'#e2efda':'#fbe4e4';}else if(a!=null){t=r.pct?`${Math.round(a)}%`:a.toFixed(1);bg='#fbe4e4';} return <td style={{ ...swCell, background: bg, textAlign: 'right', width: 52 }}>{t}</td>; })()}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {/* Bridging area (same behavior as other bridges) */}
                  <div style={{ flex: '1 1 0', minWidth: 200 }}>
                    <label style={{ fontSize: '0.65rem', fontWeight: 600, color: '#666' }}>Standard Work Bridge</label>
                    <textarea rows={12} placeholder="Notes / bridge..." value={swBridge} onChange={e => setSwBridge(e.target.value)} style={{ width: '100%', fontSize: '0.85rem', padding: 6, border: '1px solid #ccc', borderRadius: 4, resize: 'vertical' }} />
                  </div>
                </div>
                )}
              </div>
            );
          })()}
          {/* Section 6: Trailer Plan vs Actual */}
          {PATHS.map(path => {
            const completions = getCompletions(path);
            const planned = getPlannedCartons(path);
            const normalizeVid = (vid) => String(vid || '').replace(/^V/, '');

            // Plan: use frozen MIDI_PLAN (sorted by dwell, capped by LVP cartons)
            let planTrailers = [];
            if (midiPlan?.planTrailers) {
              const pathTrailers = midiPlan.planTrailers
                .filter(t => t.path === path && extractCartons(t.notes) > 0)
                .sort((a, b) => (b.dwellHours || 0) - (a.dwellHours || 0));
              let runningCartons = 0;
              for (const t of pathTrailers) {
                const c = extractCartons(t.notes);
                planTrailers.push({ ...t, cartons: c });
                runningCartons += c;
                if (planned > 0 && runningCartons >= planned) break;
              }
            }

            // Calculate SOS time from plan frozen timestamp for dwell adjustment
            const sosTime = midiPlan?.frozen ? new Date(midiPlan.frozen).getTime() / 1000 : 0;
            const getDwellAtSos = (c) => {
              if (!c || !c.checkInTime || !sosTime) return c?.dwellHours || 0;
              return (sosTime - c.checkInTime) / 3600;
            };
            const planVids = new Set(planTrailers.map(t => normalizeVid(t.visitId)));
            const completedVids = new Set(completions.map(c => normalizeVid(c.visitId)));

            // Build rows: plan on left, matched actual on right
            const rows = [];
            const unmatchedActuals = [];

            for (const pt of planTrailers) {
              const ptVid = normalizeVid(pt.visitId);
              const matchedComp = completions.find(c => normalizeVid(c.visitId) === ptVid);
              rows.push({ plan: pt, actual: matchedComp || null });
            }

            // Completions not in plan
            for (const c of completions) {
              const cVid = normalizeVid(c.visitId);
              if (!planVids.has(cVid)) {
                unmatchedActuals.push(c);
              }
            }

            if (planTrailers.length === 0 && completions.length === 0) return null;

            return (
              <div key={path} className="card" style={{ marginBottom: 8, overflowX: 'auto' }}>
                <h4 style={{ margin: '4px 0', fontSize: '0.8rem' }}>{path} — Plan: {planTrailers.length} trailers | Actual: {completions.length}</h4>
                <table style={{ borderCollapse: 'collapse', fontSize: '0.65rem', width: '100%' }}>
                  <thead>
                    <tr style={{ background: '#232f3e', color: '#fff' }}>
                      <th colSpan={4} style={{ padding: '3px 6px', textAlign: 'center', borderRight: '2px solid #fff' }}>Plan</th>
                      <th colSpan={5} style={{ padding: '3px 6px', textAlign: 'center' }}>Actuals</th>
                    </tr>
                    <tr style={{ background: '#333', color: '#fff' }}>
                      <th style={headerStyle}>Location</th><th style={headerStyle}>ISA</th><th style={headerStyle}>Dwell</th><th style={{ ...headerStyle, borderRight: '2px solid #000' }}>Cartons</th>
                      <th style={headerStyle}>Location</th><th style={headerStyle}>ISA</th><th style={headerStyle}>Dwell</th><th style={headerStyle}>In Plan</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, i) => {
                      const pt = row.plan;
                      const ct = row.actual;
                      let inPlanStatus = '';
                      let rowBg = '#fff';
                      if (ct) {
                        inPlanStatus = 'Yes'; rowBg = '#f5f5f5';
                      } else {
                        rowBg = '#fff3e0'; // Not closed - orange
                      }
                      return (
                        <tr key={i} style={{ background: i % 2 === 0 ? rowBg : rowBg }}>
                          <td style={cellStyle}>{pt.location}</td>
                          <td style={cellStyle}>{idLink(pt.vehicle || normalizeVid(pt.visitId), pt.visitId)}</td>
                          <td style={cellStyle}>{pt.dwellHours ? (pt.dwellHours/24).toFixed(1) + 'd' : '—'}</td>
                          <td style={{ ...cellStyle, borderRight: '2px solid #000' }}>{pt.cartons ? pt.cartons.toLocaleString() : '—'}</td>
                          <td style={cellStyle}>{ct?.location || ''}</td>
                          <td style={cellStyle}>{ct ? idLink(ct.isaId || ct.vehicle || normalizeVid(ct.visitId), ct.visitId) : ''}</td>
                          <td style={cellStyle}>{ct ? (getDwellAtSos(ct)/24).toFixed(1) + 'd' : ''}</td>
                          <td style={{ ...cellStyle, fontWeight: 700, color: ct ? '#4caf50' : '#e65100' }}>
                            {ct ? 'Yes' : 'Not Closed'}
                          </td>
                        </tr>
                      );
                    })}
                    {unmatchedActuals.map((c, i) => {
                      const loc = c.location || '';
                      const ddNum = loc.startsWith('DD') ? parseInt(loc.slice(2)) : 0;
                      const isPidDoor = [352,353,354,355,357,358,359,360].includes(ddNum);
                      const isPrDoor = [143,146,150,153,157].includes(ddNum);
                      const isManrDoor = [347,348].includes(ddNum);
                      const doorPath = isPidDoor ? 'PID' : isPrDoor ? 'PR' : isManrDoor ? 'MANR' : 'SKID';
                      // Only show in this path's section if door matches OR it's a cross-path reward
                      if (doorPath !== path && c.path === path) {
                        // Trailer's notes say this path but door is different - show as reward in door's section instead
                        return null;
                      }
                      if (doorPath !== path && c.path !== path) return null;
                      // "Missed" = this completed trailer dwelled SIGNIFICANTLY
                      // longer than the shortest-dwelling planned trailer for this
                      // path — more than a full day (24h) older — so it clearly
                      // slipped through the plan rather than being normal dwell
                      // noise around the youngest planned pick. Plan is sorted
                      // oldest-first, so the shortest dwell is the LAST entry.
                      const MISSED_BUFFER_HOURS = 24; // ignore <1d noise; only flag significantly-older trailers
                      const shortestPlanDwell = planTrailers.length > 0 ? (planTrailers[planTrailers.length - 1].dwellHours || 0) : 0;
                      const dwellAtSos = getDwellAtSos(c);
                      let status;
                      if (doorPath !== path) {
                        status = `✓ ${doorPath}`;
                      } else if (shortestPlanDwell > 0 && dwellAtSos > shortestPlanDwell + MISSED_BUFFER_HOURS) {
                        status = '⚠️ Missed';
                      } else {
                        status = 'Not in Plan';
                      }
                      const color = status.startsWith('✓') || status === '⚠️ Missed' ? '#4caf50' : '#c62828';
                      return (
                        <tr key={'u' + i} style={{ background: status.startsWith('✓') || status === '⚠️ Missed' ? '#e8f5e9' : '#ffcdd2' }}>
                          <td style={cellStyle}></td><td style={cellStyle}></td><td style={cellStyle}></td><td style={{ ...cellStyle, borderRight: '2px solid #000' }}></td>
                          <td style={cellStyle}>{c.location}</td>
                          <td style={cellStyle}>{idLink(c.isaId || c.vehicle || normalizeVid(c.visitId), c.visitId)}</td>
                          <td style={cellStyle}>{c.dwellHours ? (getDwellAtSos(c)/24).toFixed(1) + 'd' : '—'}</td>
                          <td style={{ ...cellStyle, fontWeight: 700, color }}>{status}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })}
          </div>
          {fclmData?.labor && (
            <div className="card" style={{ overflowX: 'auto' }}>
              <h3 className="section-title">Function Hours & Rates</h3>
              <table style={{ borderCollapse: 'collapse', fontSize: '0.7rem', width: '100%' }}>
                <thead><tr>
                  <th style={{ ...headerStyle, textAlign: 'left' }}>Function</th>
                  <th style={headerStyle}>Rate Plan</th>
                  <th style={headerStyle}>Hours Plan</th>
                  <th style={headerStyle}>Rate Actual</th>
                  <th style={headerStyle}>Hours Actual</th>
                  <th style={headerStyle}>% to Plan</th>
                  <th style={headerStyle}>Hours Δ (+/-)</th>
                </tr></thead>
                <tbody>
                  {['PID', 'AROS', 'IB PR', 'IB PIT', 'EOL', 'Fluid', 'OBPR', 'IB Support', 'OB Support'].map(group => {
                    const groupFns = (fclmData.labor || []).filter(l => l.group === group).sort((a,b) => b.total - a.total);
                    if (groupFns.length === 0) return null;
                    const groupTotal = groupFns.reduce((s, l) => s + l.total, 0);
                    const cartonMap = { 'PID': 'pid', 'AROS': 'aros', 'IB PR': 'pr', 'IB PIT': 'pit', 'EOL': 'eol', 'Fluid': 'outbound', 'OBPR': 'pr', 'IB Support': 'inbound', 'OB Support': 'outbound' };
                    const cartons = getActualCartons(cartonMap[group] || 'inbound');
                    // Rate PLAN must use TOTAL PLANNED cartons for the path (not current actuals,
                    // which would halve the plan rate mid-shift). Actual rate still uses actual cartons.
                    const planCartonMap = {
                      'PID': lvpPlan?.pidCartons, 'AROS': lvpPlan?.arosCartons, 'IB PR': lvpPlan?.prCartons,
                      'IB PIT': lvpPlan?.skidCartons, 'EOL': Math.round((lvpPlan?.pidCartons || 0) * (lvpPlan?.eolPct || 33) / 100),
                      'Fluid': lvpPlan?.totalCartons, 'OBPR': lvpPlan?.prCartons,
                      'IB Support': lvpPlan?.totalCartons, 'OB Support': lvpPlan?.totalCartons,
                    };
                    const planCartons = planCartonMap[group] || lvpPlan?.totalCartons || 0;
                    const groupRate = groupTotal > 0 ? cartons / groupTotal : 0;
                    const groupPlanHours = lvpPlan?.computedHours?.[group]?.[lvpPlan?.shiftKey] || 0;
                    const groupPlanRate = groupPlanHours > 0 ? planCartons / groupPlanHours : 0;
                    const groupPct = groupPlanRate > 0 ? (groupRate / groupPlanRate * 100) : 0;
                    const groupHoursDelta = groupPlanHours ? groupTotal - groupPlanHours : 0;
                    return (
                      <React.Fragment key={group}>
                        <tr style={{ background: '#e3f2fd', borderTop: '2px solid #000' }}>
                          <td style={{ ...cellStyle, textAlign: 'left', fontWeight: 700 }}>{group}</td>
                          <td style={{ ...cellStyle, fontWeight: 700 }}>{groupPlanRate ? groupPlanRate.toFixed(1) : '—'}</td>
                          <td style={{ ...cellStyle, fontWeight: 700 }}>{groupPlanHours ? Math.round(groupPlanHours) : '—'}</td>
                          <td style={{ ...cellStyle, fontWeight: 700 }}>{groupRate ? groupRate.toFixed(1) : '—'}</td>
                          <td style={{ ...cellStyle, fontWeight: 700 }}>{Math.round(groupTotal)}</td>
                          <td style={{ ...cellStyle, fontWeight: 700, color: groupPct >= 100 ? '#4caf50' : '#ef5350' }}>{groupPct ? `${groupPct.toFixed(0)}%` : '—'}</td>
                          <td style={{ ...cellStyle, fontWeight: 700, color: groupHoursDelta <= 0 ? '#4caf50' : '#ef5350' }}>{groupPlanHours ? (groupHoursDelta > 0 ? '+' : '') + Math.round(groupHoursDelta) : '—'}</td>
                        </tr>
                        {groupFns.map(fn => {
                          // Per-function carton basis: Sorter Wallbuild is rated on Fluid
                          // Direct; Offline Wallbuild + Fluid Load on Fluid Offline. Others
                          // use the group basis.
                          const dActF = getActualCartons('fluidDirect') || 0;
                          const oActF = getActualCartons('fluidOffline') || 0;
                          const dPlanF = lvpPlan ? Math.round(lvpPlan.pidCartons * (1 - (lvpPlan.eolPct||33)/100)) + Math.round(lvpPlan.arosCartons) : 0;
                          const oPlanF = lvpPlan ? Math.round(lvpPlan.pidCartons * (lvpPlan.eolPct||33)/100) : 0;
                          const FN_CARTON_BASIS = {
                            'Sorter Wallbuild': { act: dActF, plan: dPlanF },
                            'Offline Wallbuild': { act: oActF, plan: oPlanF },
                            'Fluid Load': { act: oActF, plan: oPlanF },
                          };
                          const basis = FN_CARTON_BASIS[fn.process_path];
                          const fnActCartons = basis ? basis.act : cartons;
                          const fnPlanCartons = basis ? basis.plan : planCartons;
                          const fnRate = fn.total > 0 ? fnActCartons / fn.total : 0;
                          const fclmToLvp = {'Offline Wallbuild': 'Wall Builder', 'Sorter Wallbuild': 'Sorter Wallbuilder', 'Wrap/Lantech': 'Pallet Wrap/Lantech', 'Fluid WS': 'Fluid WS', 'Fluid Load': 'Fluid Load', 'Jam Clear': 'Jam Clear'};
                          const lvpKey = fclmToLvp[fn.process_path] || fn.process_path;
                          const planHours = (lvpPlan?.computedHours?.[lvpKey]?.[lvpPlan?.shiftKey]) || (lvpPlan?.computedHours?.[fn.process_path]?.[lvpPlan?.shiftKey]) || 0;
                          const planRate = planHours > 0 ? fnPlanCartons / planHours : 0;
                          const pctToPlan = planRate > 0 ? (fnRate / planRate * 100) : 0;
                          const hoursDelta = planHours ? fn.total - planHours : 0;
                          return (
                            <tr key={fn.process_path} style={{ borderBottom: '1px solid #eee' }}>
                              <td style={{ ...cellStyle, textAlign: 'left', paddingLeft: 16 }}>{fn.process_path}</td>
                              <td style={cellStyle}>{planRate ? planRate.toFixed(1) : '—'}</td>
                              <td style={cellStyle}>{planHours ? planHours.toFixed(1) : '—'}</td>
                              <td style={cellStyle}>{fnRate ? fnRate.toFixed(1) : '—'}</td>
                              <td style={cellStyle}>{fn.total.toFixed(1)}</td>
                              <td style={{ ...cellStyle, color: pctToPlan >= 100 ? '#4caf50' : pctToPlan > 0 ? '#ef5350' : '#333' }}>{pctToPlan ? `${pctToPlan.toFixed(0)}%` : '—'}</td>
                              <td style={{ ...cellStyle, color: hoursDelta <= 0 ? '#4caf50' : '#ef5350', fontWeight: 600 }}>{planHours ? (hoursDelta > 0 ? '+' : '') + hoursDelta.toFixed(1) : '—'}</td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Admin Hours: planned (LVP daily goal, broken out by category/function) vs actual (FCLM residual) */}
          {fclmData && (
            <div className="card" style={{ overflowX: 'auto' }}>
              <h3 className="section-title">Admin Hours</h3>
              {(() => {
                const sk = lvpPlan?.shiftKey || '';
                const ad = lvpPlan?.adminDetail || {};
                const pf = (fn) => parseFloat(ad[fn]?.[sk]) || 0; // planned hours for a function this shift
                const aa = fclmData.admin_actual || {};
                const hasActual = Object.keys(aa).length > 0;
                const af = (fn) => aa[fn] || 0; // actual hours for a function this shift
                const laborTotal = (fclmData.labor || []).reduce((s, l) => s + (l.total || 0), 0);
                const residualAdmin = Math.max(0, totalHours - laborTotal);
                const grandPlanned = ADMIN_CATEGORIES.reduce((s, c) => s + c.functions.reduce((a, fn) => a + pf(fn), 0), 0);
                const grandActual = hasActual ? ADMIN_CATEGORIES.reduce((s, c) => s + c.functions.reduce((a, fn) => a + af(fn), 0), 0) : residualAdmin;
                const grandDelta = grandActual - grandPlanned;
                const dcell = (d) => (d > 0 ? '+' : '') + Math.round(d).toLocaleString();
                return (
                  <table style={{ borderCollapse: 'collapse', fontSize: '0.72rem' }}>
                    <thead><tr>
                      <th style={{ ...headerStyle, textAlign: 'left', minWidth: 200 }}>Category / Function</th>
                      <th style={headerStyle}>Planned</th><th style={headerStyle}>Actual</th><th style={headerStyle}>Δ (+/-)</th>
                    </tr></thead>
                    <tbody>
                      {ADMIN_CATEGORIES.map(cat => {
                        const catP = cat.functions.reduce((a, fn) => a + pf(fn), 0);
                        const catA = cat.functions.reduce((a, fn) => a + af(fn), 0);
                        return (
                          <React.Fragment key={cat.label}>
                            <tr style={{ background: cat.color || '#f5f5f5' }}>
                              <td style={{ ...cellStyle, textAlign: 'left', fontWeight: 700 }}>{cat.label}</td>
                              <td style={{ ...cellStyle, fontWeight: 700 }}>{catP ? Math.round(catP).toLocaleString() : '—'}</td>
                              <td style={{ ...cellStyle, fontWeight: 700 }}>{hasActual ? (catA ? Math.round(catA).toLocaleString() : '—') : '—'}</td>
                              <td style={{ ...cellStyle, fontWeight: 700, color: hasActual && catP ? (catA - catP <= 0 ? '#4caf50' : '#ef5350') : '#333' }}>{hasActual && (catP || catA) ? dcell(catA - catP) : '—'}</td>
                            </tr>
                            {cat.functions.map(fn => (
                              <tr key={fn}>
                                <td style={{ ...cellStyle, textAlign: 'left', paddingLeft: 18, color: '#555' }}>{fn}</td>
                                <td style={cellStyle}>{pf(fn) ? Math.round(pf(fn) * 10) / 10 : '—'}</td>
                                <td style={cellStyle}>{hasActual ? (af(fn) ? Math.round(af(fn) * 10) / 10 : '—') : '—'}</td>
                                <td style={{ ...cellStyle, color: hasActual && (pf(fn) || af(fn)) ? (af(fn) - pf(fn) <= 0 ? '#4caf50' : '#ef5350') : '#333' }}>{hasActual && (pf(fn) || af(fn)) ? dcell(af(fn) - pf(fn)) : '—'}</td>
                              </tr>
                            ))}
                          </React.Fragment>
                        );
                      })}
                      <tr style={{ borderTop: '2px solid #000', fontWeight: 700 }}>
                        <td style={{ ...cellStyle, textAlign: 'left', fontWeight: 700 }}>GRAND TOTAL</td>
                        <td style={{ ...cellStyle, fontWeight: 700 }}>{grandPlanned ? Math.round(grandPlanned).toLocaleString() : '—'}</td>
                        <td style={{ ...cellStyle, fontWeight: 700 }}>{grandActual ? Math.round(grandActual).toLocaleString() : '—'}</td>
                        <td style={{ ...cellStyle, fontWeight: 700, color: grandPlanned ? (grandDelta <= 0 ? '#4caf50' : '#ef5350') : '#333' }}>{grandPlanned ? dcell(grandDelta) : '—'}</td>
                      </tr>
                    </tbody>
                  </table>
                );
              })()}
              <div style={{ fontSize: '0.62rem', color: '#888', marginTop: 4 }}>Planned = LVP admin-hours detail for this shift. Actual = FCLM Admin/HR/IT (process 01002960) hours per function, pulled each 5-min sync. If a shift's cache predates this feature, only the GRAND TOTAL actual shows (FC hours − direct/support labor).</div>
            </div>
          )}

        </>
      )}

    </div>
  );
}
